import React, { useState, useEffect } from 'react';
import { X, Search, Package2, Plus } from 'lucide-react';
import { getMaterials, Material } from '../../lib/materials';
import { useBusiness } from '../../contexts/BusinessContext';
import MaterialModal from '../MaterialModal';

interface SelectMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (material: Material, quantity: number) => void;
  businessId?: string;
}

export default function SelectMaterialModal({
  isOpen,
  onClose,
  onAdd,
  businessId
}: SelectMaterialModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMaterials, setSelectedMaterials] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const loadMaterials = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getMaterials(selectedBusiness.id);
      setMaterials(data);
      setError(null);
    } catch (err) {
      console.error('Error loading materials:', err);
      setError(err instanceof Error ? err.message : 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadMaterials();
    }
  }, [isOpen, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedMaterials(new Set());
      setQuantities({});
    }
  };

  const filteredMaterials = materials.filter(material => {
    const searchLower = searchQuery.toLowerCase();
    return (
      material.name.toLowerCase().includes(searchLower) ||
      material.description?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddMaterial = (material: Material) => {
    const quantity = quantities[material.id] || 1;
    onAdd(material, quantity);
    
    // Add to selected materials
    setSelectedMaterials(prev => {
      const next = new Set(prev);
      next.add(material.id);
      return next;
    });
    // Close modal after successful add
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-50`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Select Material</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Material
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading materials...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-6">
                <div className="text-red-600 mb-2">{error}</div>
                <button
                  onClick={loadMaterials}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Try again
                </button>
              </div>
            ) : filteredMaterials.length === 0 ? (
              <div className="text-center py-6">
                <Package2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">
                  {searchQuery ? 'No matching materials' : 'No materials available'}
                </p>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add materials to track costs and inventory'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Material
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMaterials.map((material) => {
                  const isSelected = selectedMaterials.has(material.id);
                  return (
                    <div key={material.id} className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">{material.name}</h3>
                          {material.description && (
                            <p className="mt-1 text-sm text-gray-500">{material.description}</p>
                          )}
                          <div className="mt-2 text-xs text-gray-500">
                            ${material.cost.toFixed(2)} per {material.unit}
                          </div>
                          {!isSelected && (
                            <div className="mt-2 flex items-center space-x-2">
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={quantities[material.id] || ''}
                                placeholder="Quantity"
                                onChange={(e) => setQuantities(prev => ({
                                  ...prev,
                                  [material.id]: parseFloat(e.target.value) || 0
                                }))}
                                className="w-20 h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                              />
                              <span className="text-sm text-gray-500">{material.unit}(s)</span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddMaterial(material)}
                          disabled={isSelected || !quantities[material.id] || quantities[material.id] > material.quantity}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            isSelected
                              ? 'bg-green-100 text-green-800 cursor-not-allowed'
                              : !quantities[material.id] || quantities[material.id] <= 0 || quantities[material.id] > material.quantity
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'text-white bg-gray-900 hover:bg-gray-800'
                          }`}
                        >
                          {isSelected ? 'Added' : 'Add'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <MaterialModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadMaterials();
        }}
        businessId={selectedBusiness?.id}
        mode="create"
      />
    </div>
  );
}